/**
 * The weapon altar — the opening choice of a run.
 *
 * **One platform, one weapon, cycling every 30 seconds.** The offer floats above
 * the plinth as an actual object; stand under it to read its stats, press E to
 * take it. Wait, and it turns over into something else.
 *
 * The single slot is what makes it a decision rather than a menu. Three offers
 * side by side is a comparison you solve; one offer on a clock is a gamble
 * against your own patience — take the adequate thing in front of you, or let
 * it go and find out what comes next.
 *
 * The altar owns no rendering and no input handling; it exposes state and the
 * integrator drives it. That keeps it testable without a browser.
 */

import type { Rng } from '../core/rng';
import type { ModuleDef, WeaponDef, WeaponInstance, ResolvedWeapon } from '../combat/types';
import { resolveWeapon } from '../combat/resolve';
import { hitRateHz } from '../combat/weaponData';
import { rollWeapon } from '../combat/wielder';
import { MAT } from '../sim/materialData';
import { Flag } from '../sim/materials';
import type { CellWorld } from '../sim/world';

/** Seconds between re-rolls of unclaimed offers. */
export const REROLL_SECONDS = 30;

/** How close the player must stand to read and claim an offer. */
export const CLAIM_RANGE = 22;

export interface AltarOffer {
  /** World position of the pedestal top. */
  x: number;
  y: number;
  instance: WeaponInstance;
  resolved: ResolvedWeapon;
}

export interface AltarStatus {
  active: boolean;
  /** The single current offer. */
  offer: AltarOffer | null;
  /** True when the player is close enough to read and take it. */
  inRange: boolean;
  /** Seconds until this weapon is replaced. */
  timeToReroll: number;
  rerolls: number;
}

export class WeaponAltar {
  /** The one pedestal's current offer. */
  offer: AltarOffer;
  active = true;
  inRange = false;
  rerolls = 0;
  private timer = REROLL_SECONDS;
  private weapons: WeaponDef[];
  private modules: ReadonlyMap<string, ModuleDef>;
  private rng: Rng;

  /** Fired when the player claims an offer. */
  onClaim: ((offer: AltarOffer) => void) | null = null;
  /** Fired on each re-roll, for the event ticker. */
  onReroll: (() => void) | null = null;

  constructor(
    weapons: ReadonlyMap<string, WeaponDef>,
    modules: ReadonlyMap<string, ModuleDef>,
    rng: Rng,
    x: number,
    y: number,
  ) {
    // Only melee weapons are offered here: the off-hand already ships a pistol,
    // so the altar's job is to choose the half of the pair that has a motion.
    // A projectile on the plinth would also have nothing to show in-world.
    const melee = [...weapons.values()].filter((w) => w.archetype === 'melee');
    this.weapons = melee.length > 0 ? melee : [...weapons.values()];
    this.modules = modules;
    this.rng = rng;
    this.offer = this.makeOffer(x, y);
  }

  private makeOffer(x: number, y: number): AltarOffer {
    const def = this.rng.pick(this.weapons);
    const instance = rollWeapon(def.id, this.rng, 1);
    return { x, y, instance, resolved: resolveWeapon(def, instance, this.modules) };
  }

  /**
   * Advance the re-roll clock and update which offer the player is at.
   * `dt` in seconds.
   */
  update(dt: number, playerX: number, playerY: number): void {
    if (!this.active) return;

    this.timer -= dt;
    if (this.timer <= 0) {
      this.timer = REROLL_SECONDS;
      this.rerolls++;
      this.offer = this.makeOffer(this.offer.x, this.offer.y);
      if (this.onReroll) this.onReroll();
    }

    const dx = this.offer.x - playerX;
    const dy = this.offer.y - playerY;
    this.inRange = dx * dx + dy * dy < CLAIM_RANGE * CLAIM_RANGE;
  }

  /** Take the current offer. Returns it, or null if out of range. */
  claim(): AltarOffer | null {
    if (!this.active || !this.inRange) return null;
    const offer = this.offer;
    this.active = false;
    this.inRange = false;
    if (this.onClaim) this.onClaim(offer);
    return offer;
  }

  status(): AltarStatus {
    return {
      active: this.active,
      offer: this.active ? this.offer : null,
      inRange: this.inRange,
      timeToReroll: Math.max(0, this.timer),
      rerolls: this.rerolls,
    };
  }

  /**
   * Build the physical plinths out of cells.
   *
   * Deliberately made of real material rather than drawn as decoration: the
   * altar is part of the world, so it can be burned, corroded and blown up like
   * anything else. `RUNESTONE` is the right choice — it is hard, sacred, and
   * anchors local reality, which is a quiet justification for why the one
   * guaranteed structure in a procedural run does not get eaten by the sim.
   */
  buildPlinths(cells: CellWorld, fromX?: number): void {
    {
      const o = this.offer;
      const bx = Math.round(o.x);
      const by = Math.round(o.y);

      // The terrace. `spawnPoint()` now returns a verified point on the
      // surface, so this is no longer load-bearing against a groundless spawn
      // — but it still guarantees the approach is walkable regardless of what
      // the heightfield did locally, and the sills below still keep a biome's
      // liquid off the plinth for the length of the choice.
      const left = fromX === undefined ? bx - 5 : Math.min(bx, Math.round(fromX)) - 8;
      const right = fromX === undefined ? bx + 5 : Math.max(bx, Math.round(fromX)) + 8;

      // Clear headroom so the player can stand here.
      for (let y = by - 24; y < by; y++) {
        for (let x = left; x <= right; x++) cells.set(x, y, 0);
      }
      for (let y = by; y < by + 3; y++) {
        for (let x = left; x <= right; x++) {
          // The runestone core still marks the plinth proper — the terrace
          // reads as approach, the plinth as destination.
          const onPlinth = x >= bx - 5 && x <= bx + 5;
          const edge = y === by || x === bx - 5 || x === bx + 5;
          cells.set(x, y, onPlinth && edge ? MAT.RUNESTONE : MAT.CERAMIC);
          cells.addFlag(x, y, Flag.STATIC);
        }
      }
      // Solid footing beneath, so the plinth never falls into a cave.
      for (let y = by + 3; y < by + 10; y++) {
        for (let x = left + 1; x <= right - 1; x++) {
          cells.set(x, y, MAT.GRANITE);
          cells.addFlag(x, y, Flag.STATIC);
        }
      }

      // Sills at both ends of the terrace.
      //
      // Clearing the volume is not enough once the player is part of the
      // simulation: a biome's liquid flows straight back into it, and the
      // altar asks you to *stand still for thirty seconds* while you choose.
      // Observed in Verdigris Spire — acid ran across the terrace and took the
      // player from 100 HP to 7 before the first weapon was claimed. The
      // opening decision of a run must not be a damage race, so the terrace
      // gets walls tall enough to turn lateral flow away.
      for (let y = by - 14; y < by; y++) {
        for (const x of [left - 1, left - 2, right + 1, right + 2]) {
          cells.set(x, y, MAT.RUNESTONE);
          cells.addFlag(x, y, Flag.STATIC);
        }
      }
    }
  }
}

/**
 * One-line stat summary for the offer panel.
 *
 * The rate is the **opener** — one light node run windup → active → recovery,
 * the rate a player gets from clean, spaced attacks, and the rate #39 named
 * canonical. It used to print `resolved.cadence`, whose base is unitless: it is
 * the reference point of the `cadenceMul` chain and cancels out of its own
 * formula, so the opening decision of a run was made against a `/s` figure no
 * weapon has ever swung at (`ashen_sword` read 2.10/s and opens at 7.81/s).
 *
 * Through the shipping `hitRateHz`, never a local copy of it: this altar is
 * the first place a player compares two weapons, so it must not be able to
 * disagree with the combat model — or with the weapon panel, which reads the
 * same function.
 */
export function describeOffer(o: AltarOffer): string {
  const r = o.resolved;
  const rate = hitRateHz(r);
  return (
    `${r.damage.toFixed(0)} dmg · ${rate.toFixed(2)}/s · ` +
    `${r.reach.toFixed(0)} reach · ${(r.critChance * 100).toFixed(0)}% crit`
  );
}

/**
 * How this roll compares to the weapon's baseline, as signed percentages.
 * The comparison is the point — a player cannot judge "94 damage" without it.
 */
export function rollDeltas(o: AltarOffer): Array<{ label: string; pct: number }> {
  const s = o.instance.rolled;
  return [
    { label: 'dmg', pct: (s.damageMul - 1) * 100 },
    { label: 'rate', pct: (s.cadenceMul - 1) * 100 },
    { label: 'reach', pct: (s.reachMul - 1) * 100 },
    { label: 'crit', pct: s.critChanceAdd * 100 },
  ];
}
